import { useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { StoreRootState } from 'services/store/types';
import { actions } from 'services/store/ai';
import { IBaseSong } from 'services/api/entities';
import useAudio from '../../useAudio';

const useTrack = (song: IBaseSong) => {
  const { id, duration, musicFilePath } = song;
  const dispatch = useDispatch();
  const { audioElement, setSrc, play, pause, seek } = useAudio();
  const currentTrack = useSelector(
    (store: StoreRootState) => store.ai.currentTrack
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const isCurrentTrack = useMemo(() => id === currentTrack, [currentTrack, id]);

  const onPlayHandler = useCallback(() => {
    setIsPlaying(true);
  }, [setIsPlaying]);

  const onPauseHandler = useCallback(() => {
    setIsPlaying(false);
  }, [setIsPlaying]);

  const onEndedHandler = useCallback(() => {
    setProgress(0);
  }, [setProgress]);

  useEffect(() => {
    if (!isCurrentTrack || audioElement?.paused) {
      return;
    }
    setIsPlaying(true);
  }, [isCurrentTrack, audioElement, setIsPlaying]);

  const onTimeUpdateHandler = useCallback(() => {
    if (!audioElement) {
      return;
    }
    const currentProgress = audioElement.currentTime / audioElement.duration;
    setProgress(currentProgress);
  }, [audioElement, setProgress]);

  useEffect(() => {
    if (!audioElement) {
      return;
    }
    if (isCurrentTrack) {
      audioElement.addEventListener('play', onPlayHandler);
      audioElement.addEventListener('pause', onPauseHandler);
      audioElement.addEventListener('ended', onEndedHandler);
      audioElement.addEventListener('timeupdate', onTimeUpdateHandler);

      return () => {
        audioElement.removeEventListener('play', onPlayHandler);
        audioElement.removeEventListener('pause', onPauseHandler);
        audioElement.removeEventListener('ended', onEndedHandler);
        audioElement.removeEventListener('timeupdate', onTimeUpdateHandler);
        setProgress(0);
      };
    }
  }, [
    audioElement,
    onPlayHandler,
    onPauseHandler,
    onEndedHandler,
    onTimeUpdateHandler,
    isCurrentTrack,
  ]);

  if (isCurrentTrack) {
    return {
      duration,
      isPlaying,
      progress,
      play,
      pause,
      seek,
    };
  }

  return {
    duration,
    isPlaying: false,
    progress: 0,
    play: () => {
      setSrc(musicFilePath);
      play();
      dispatch(actions.setCurrentTrack({ id }));
    },
    seek: (value: number) => {
      setSrc(musicFilePath);
      play();
      seek(value);
      dispatch(actions.setCurrentTrack({ id }));
    },

    pause: () => {},
  };
};

export default useTrack;
